<template>
  <div class="col-xl-12">
    <div class="appointment">
      <div class="card">
        <div class="card-header card-no-border">
          <div class="header-top">
            <h5 class="m-0 text-muted">Change Password</h5>
          </div>
        </div>
        <div class="card-body">
          <form @submit.prevent="handleChangePassword">
            <!--Add Current Password Textbox-->
            <div class="form-group">
              <label for="currentPassword">Current Password <span>*</span></label>
              <div class="input-group">
                <input
                    id="currentPassword"
                    :type="showCurrentPassword ? 'text' : 'password'"
                    class="form-control"
                    v-model="form.currentPassword"
                    required
                />
                <!--Password Show and Hide Button-->
                <button type="button" style="width:50px; border: 1px solid #dee2e6; border-radius: 0 5px 5px 0; background-color: #fff;" @click="toggleShowCurrentPassword">
                  <i :class="showCurrentPassword ? 'fa fa-regular fa-eye' : 'fa fa-regular fa-eye-slash'"></i>
                </button>
              </div>
            </div>

            <!--Add New Password Textbox-->
            <div class="form-group">
              <label for="newPassword">New Password <span>*</span></label>
              <div class="input-group">
                <input
                    id="newPassword"
                    :type="showNewPassword ? 'text' : 'password'"
                    class="form-control"
                    v-model="form.newPassword"
                    @input="evaluateNewPassword"
                    required
                />
                <!--Password Show and Hide Button-->
                <button type="button" style="width:50px; border: 1px solid #dee2e6; border-radius: 0 5px 5px 0; background-color: #fff;" @click="toggleShowNewPassword">
                  <i :class="showNewPassword ? 'fa fa-regular fa-eye' : 'fa fa-regular fa-eye-slash'"></i>
                </button>
              </div>
              <!--Password Policy Checking and Password Strength Indicators-->
              <div v-if="form.newPassword.length !== 0" class="progress mt-2" style="height: 5px; border-radius: 5px;">
                <div :class="['progress-bar', newPasswordStrengthLabel === 'Strong' ? 'bg-success' : 'bg-danger']" :style="{ width: newPasswordStrength + '%' }"></div>
              </div>
              <div v-if="form.newPassword.length !== 0" class="d-flex justify-content-between mt-1">
                <small :class="newPasswordStrengthLabel === 'Weak' ? 'text-danger' : 'text-success'" class="text-start">
                  {{ newPasswordErrors }}
                </small>
                <small :class="newPasswordStrengthLabel === 'Weak' ? 'text-danger' : 'text-success'" class="text-end">
                  {{ newPasswordStrengthLabel }}
                </small>
              </div>
            </div>

            <!--Add Confirm Password Textbox-->
            <div class="form-group">
              <label for="confirmPassword">Confirm Password <span>*</span></label>
              <div class="input-group">
                <input
                    id="confirmPassword"
                    :type="showConfirmPassword ? 'text' : 'password'"
                    class="form-control"
                    v-model="form.confirmPassword"
                    @input="evaluateConfirmPassword"
                    required
                />
                <!--Password Show and Hide Button-->
                <button type="button" style="width:50px; border: 1px solid #dee2e6; border-radius: 0 5px 5px 0; background-color: #fff;" @click="toggleShowConfirmPassword">
                  <i :class="showConfirmPassword ? 'fa fa-regular fa-eye' : 'fa fa-regular fa-eye-slash'"></i>
                </button>
              </div>
              <!--Password Policy Checking and Password Strength Indicators-->
              <div v-if="form.confirmPassword.length !== 0" class="progress mt-2" style="height: 5px; border-radius: 5px;">
                <div :class="['progress-bar', confirmPasswordStrengthLabel === 'Strong' ? 'bg-success' : 'bg-danger']" :style="{ width: confirmPasswordStrength + '%' }"></div>
              </div>
              <div v-if="form.confirmPassword.length !== 0" class="d-flex justify-content-between mt-1">
                <small :class="confirmPasswordStrengthLabel === 'Weak' ? 'text-danger' : 'text-success'" class="text-start">
                  {{ confirmPasswordErrors }}
                </small>
                <small :class="confirmPasswordStrengthLabel === 'Weak' ? 'text-danger' : 'text-success'" class="text-end">
                  {{ confirmPasswordStrengthLabel }}
                </small>
              </div>
            </div>

            <!--Change Password Button-->
            <div class="d-grid">
              <button class="btn button-default btn-block w-100" :disabled="!canConfirm || loading">
                <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span v-else>Change Password</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { changePassword } from '@/services/dataService';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';

export default {
  data() {
    return {
      //Main Variables
      form: {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
        changePasswordError: ""
      },

      //Password Show and Hide
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,

      //Status Handling
      loading: false, // Loading state
      error: null, // Error state

      //Password Policy Checking
      newPasswordStrength: 0,
      newPasswordErrors: 'Password Strength: ',
      newPasswordStrengthLabel: 'Weak',
      confirmPasswordStrength: 0,
      confirmPasswordErrors: 'Password Strength: ',
      confirmPasswordStrengthLabel: 'Weak',
    };
  },

  setup() {
    const router = useRouter();
    const toast = useToast(); // Initialize the toaster
    return { router, toast };
  },

  computed: {
    //Password Change button active and inactive handling
    canConfirm() {
      return (
          this.form.currentPassword &&
          this.form.newPassword &&
          this.form.confirmPassword &&
          this.form.newPassword === this.form.confirmPassword && this.form.currentPassword !== (this.form.newPassword && this.form.confirmPassword)
      );
    },
  },

  methods: {
    //Hide and show current password
    toggleShowCurrentPassword() {
      this.showCurrentPassword = !this.showCurrentPassword;
    },

    //Hide and show new password
    toggleShowNewPassword() {
      this.showNewPassword = !this.showNewPassword;
    },

    //Hide and show confirm password
    toggleShowConfirmPassword() {
      this.showConfirmPassword = !this.showConfirmPassword;
    },

    //New Password Field Validations
    evaluateNewPassword() {
      const password = this.form.newPassword;
      const errors = [];
      let didMatchError = false;

      // Check criteria in order of priority and enqueue errors
      if (password.length < 10) {
        errors.push("Password must be at least 10 characters long.");
      }
      if (!/[a-z]/.test(password)) {
        errors.push("Password must include at least one lowercase letter.");
      }
      if (!/[A-Z]/.test(password)) {
        errors.push("Password must include at least one uppercase letter.");
      }
      if (!/[0-9]/.test(password)) {
        errors.push("Password must include at least one number (0-9).");
      }
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        errors.push("Password must include at least one special character.");
      }
      if (password === this.form.currentPassword) {
        errors.push("New password should not be the same as the current password.");
        didMatchError = true; // Set flag for mismatch error
      }

      // Set the first error from the queue or a success message
      this.newPasswordErrors = errors.length ? errors[0] : "Password Policy Accepted.";

      // Update strength based on criteria met
      let strength = 0;
      if (password.length >= 10) strength += 20;
      if (/[a-z]/.test(password)) strength += 20;
      if (/[A-Z]/.test(password)) strength += 20;
      if (/[0-9]/.test(password)) strength += 20;
      if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 20;

      this.newPasswordStrength = strength;
      this.newPasswordStrengthLabel = strength === 100 ? "Strong" : "Weak";
      this.newPasswordStrengthLabel = didMatchError ? "Weak" : strength === 100 ? "Strong" : "Weak";
    },

    //Confirm Password Field Validations
    evaluateConfirmPassword() {
      const password = this.form.confirmPassword;
      const errors = [];
      let mismatchError = false;
      let didMatchError = false;

      // Check criteria in order of priority and enqueue errors
      if (password.length < 10) {
        errors.push("Password must be at least 10 characters long.");
      }
      if (!/[a-z]/.test(password)) {
        errors.push("Password must include at least one lowercase letter.");
      }
      if (!/[A-Z]/.test(password)) {
        errors.push("Password must include at least one uppercase letter.");
      }
      if (!/[0-9]/.test(password)) {
        errors.push("Password must include at least one number (0-9).");
      }
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        errors.push("Password must include at least one special character.");
      }
      if (password === this.form.currentPassword) {
        errors.push("New password should not be the same as the current password.");
        didMatchError = true; // Set flag for mismatch error
      }
      if (this.form.newPassword !== this.form.confirmPassword) {
        errors.push("New password and confirm password should be the same.");
        mismatchError = true; // Set flag for mismatch error
      }

      // Set the first error from the queue or a success message
      this.confirmPasswordErrors = errors.length ? errors[0] : "Password Policy Accepted.";

      // Update strength based on criteria met
      let strength = 0;
      if (password.length >= 10) strength += 20;
      if (/[a-z]/.test(password)) strength += 20;
      if (/[A-Z]/.test(password)) strength += 20;
      if (/[0-9]/.test(password)) strength += 20;
      if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 20;

      this.confirmPasswordStrength = strength;
      this.confirmPasswordStrengthLabel = (didMatchError || mismatchError) ? "Weak" : strength === 100 ? "Strong" : "Weak";
    },

    //Password Change API Integration
    async handleChangePassword() {
      this.form.changePasswordError = ''; // change password Error message

      this.loading = true; // Set loading to true when reset password starts

      try {
        //  Retrieve token from localStorage
        this.token = localStorage.getItem('token_set');

        // Retrieve userId from localStorage
        const storedUserId = localStorage.getItem('user_id');

        // Reset Password API call
        const userResponse = await changePassword(this.token, storedUserId, this.form.currentPassword, this.form.newPassword, this.form.confirmPassword);

        // Handle successful change password
        this.toast.success(userResponse.data.message);
        this.$router.push('/dashboard');

      } catch (error) {
        // Handle invalid credentials
        if (error.response && error.response.data && error.response.data.message) {
          this.form.changePasswordError = error.response.data.message;
          this.toast.error(this.form.changePasswordError); // Show toaster for login error
        } else {
          this.form.changePasswordError = "An error occurred. Please try again.";
          this.toast.error(this.form.changePasswordError); // Show toaster for general error
        }
        console.error('API Error:', error);
      } finally {
        this.loading = false; // Set loading to false when login completes
      }
    },
  },
};
</script>

<style>
.form-group {
  margin-bottom: 2rem;
}

.form-group label {
  font-weight: 500;
}

.form-group span {
  color: #f00 !important;
}
</style>
